import { cn } from "@/lib/utils";
import type { IconProps } from "@/types/general";
import { getComponentSize } from "@/types/general";
import React from "react";
import * as IconComponents from "@/icons/component";
import { isValidIconName } from "@/icons/icon-list";

interface FallbackIconProps extends React.SVGProps<SVGSVGElement> {
  title?: string;
}

const FallbackIcons = {
  Warning: ({ title = "Warning", ...props }: FallbackIconProps) => (
    <svg 
      viewBox="0 0 24 24" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg" 
      aria-label={title}
      {...props}
    >
      <path 
        d="M12 2L1 21h22L12 2zm0 4l7.53 13H4.47L12 6zm-1 5v4h2v-4h-2zm0 6v2h2v-2h-2z" 
        fill="currentColor" 
      />
    </svg>
  ),
  
  Error: ({ title = "Error", ...props }: FallbackIconProps) => (
    <svg 
      viewBox="0 0 24 24" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg" 
      aria-label={title}
      {...props}
    >
      <path 
        d="M12 4C7.58 4 4 7.58 4 12C4 16.42 7.58 20 12 20C16.42 20 20 16.42 20 12C20 7.58 16.42 4 12 4ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" 
        fill="currentColor" 
      />
    </svg>
  ),
};

const formatIconName = (name: string): string => {
  if (name.includes(':') === false) {
    return name;
  }
  
  const [prefix = '', iconName = ''] = name.split(':');
  
  const formatPart = (part: string): string => 
    part
      .split('-')
      .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join('');
  
  return formatPart(prefix) + formatPart(iconName);
};

const Icon = React.forwardRef<HTMLDivElement, IconProps>(
  ({ icon, className, size = 'sm', ...props }, ref) => {
    const pixelSize = getComponentSize(size);
    
    if (isValidIconName(icon) === false) {
      console.error(
        `Icon "${icon}" is not defined in the icon list. Add it to src/icons/icon-list.ts and run the icon generation script.`
      );
      
      return (
        <div 
          ref={ref} 
          className={cn("flex items-center justify-center text-destructive", className)} 
          {...props}
        >
          <FallbackIcons.Error 
            width={pixelSize} 
            height={pixelSize} 
            title={`Icon "${icon}" not found`}
          />
        </div>
      );
    }
    
    const componentName = formatIconName(icon);
    const IconComponent = (IconComponents as unknown as { [key: string]: React.ComponentType<React.SVGProps<SVGSVGElement>> })[componentName];
    
    if (typeof IconComponent === 'undefined') {
      console.error(
        `Icon component "${componentName}" for "${icon}" not found. Please run 'npm run generate:icons' to generate icon components.`
      );
      
      return (
        <div 
          ref={ref} 
          className={cn("flex items-center justify-center text-warning", className)} 
          {...props}
        >
          <FallbackIcons.Warning 
            width={pixelSize} 
            height={pixelSize} 
            title={`Icon not found: ${icon}`}
          />
        </div>
      );
    }
    
    return (
      <div 
        ref={ref} 
        className={cn("flex items-center justify-center", className)} 
        {...props}
      >
        <IconComponent width={pixelSize} height={pixelSize} />
      </div>
    );
  }
);

Icon.displayName = "Icon";

export { Icon }; 